import React, { useCallback, useEffect, useState } from 'react';
import { getUsers } from '../../services/adminService';
import UserDetailDrawer from '../../components/Admin/UserDetailDrawer/UserDetailDrawer';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const AdminUsers = () => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUsers({
        query,
        status: statusFilter || null,
        role: roleFilter || null,
        page,
        pageSize,
      });
      setRows(data.users || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Eroare la incarcarea utilizatorilor.');
    } finally {
      setLoading(false);
    }
  }, [query, statusFilter, roleFilter, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2>Utilizatori înregistrați ({totalCount})</h2>
      </div>

      <form
        className="admin-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setQuery(queryInput);
        }}
      >
        <input
          type="search"
          placeholder="Caută după email, nume sau companie"
          value={queryInput}
          onChange={(e) => setQueryInput(e.target.value)}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Toți utilizatorii</option>
          <option value="paid">Doar plătiți (abonament activ)</option>
          <option value="free">Doar fără abonament</option>
        </select>
        <select
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Toate rolurile</option>
          <option value="Admin">Doar Admin</option>
          <option value="CRM">Doar CRM</option>
          <option value="User">Doar User</option>
        </select>
        <button type="submit" className="admin-button">
          Caută
        </button>
      </form>

      {loading ? (
        <div className="admin-empty">Se încarcă…</div>
      ) : error ? (
        <div className="admin-empty">⚠️ {error}</div>
      ) : rows.length === 0 ? (
        <div className="admin-empty">Nu există utilizatori care să corespundă criteriilor.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Nume / Companie</th>
                <th>Telefon</th>
                <th>Înregistrat</th>
                <th>Rol</th>
                <th>Status</th>
                <th>Credite</th>
                <th>Email conf.</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => setSelectedUser(u)}
                  style={{ cursor: 'pointer' }}
                  title="Click pentru detalii"
                >
                  <td>{u.email}</td>
                  <td>
                    {u.name}
                    {u.company && (
                      <>
                        <br />
                        <small style={{ color: '#94a3b8' }}>{u.company}</small>
                      </>
                    )}
                  </td>
                  <td>{u.phone || '—'}</td>
                  <td>{fmtDate(u.dateCreated)}</td>
                  <td>
                    {(u.roles || []).length === 0 ? (
                      <span style={{ color: '#94a3b8' }}>—</span>
                    ) : (
                      (u.roles || []).map((r) => (
                        <span
                          key={r}
                          className={`admin-status ${r === 'Admin' ? 'contacted' : 'free'}`}
                          style={{ marginRight: 4 }}
                        >
                          {r.toUpperCase()}
                        </span>
                      ))
                    )}
                  </td>
                  <td>
                    {u.hasActiveSubscription ? (
                      <span className="admin-status paid">PLĂTIT</span>
                    ) : (
                      <span className="admin-status free">FREE</span>
                    )}
                    {u.subscriptionEndDate && (
                      <>
                        <br />
                        <small style={{ color: '#94a3b8' }}>
                          până la {fmtDate(u.subscriptionEndDate)}
                        </small>
                      </>
                    )}
                  </td>
                  <td>
                    <strong>{u.totalCredits}</strong>
                    <br />
                    <small style={{ color: '#94a3b8' }}>
                      {u.subscriptionCredits} abon. + {u.purchasedCredits} extra
                    </small>
                  </td>
                  <td>{u.emailConfirmed ? '✓' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="admin-pagination">
        <button
          className="admin-button secondary"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1 || loading}
        >
          ‹ Anterior
        </button>
        <span>
          Pagina {page} din {totalPages}
        </span>
        <button
          className="admin-button secondary"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages || loading}
        >
          Următor ›
        </button>
      </div>

      <UserDetailDrawer
        user={selectedUser}
        isOpen={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        onChanged={() => {
          // Refresh the row data so the drawer's badges (roles, emailConfirmed)
          // match the new server state after a Phase 3 action lands.
          load();
        }}
      />
    </div>
  );
};

export default AdminUsers;
